import { useEffect, useRef } from 'react';
import * as THREE from 'three';

/**
 * Fumaça atmosférica com partículas em Three.js atrás do card de login —
 * técnica inspirada num efeito de fumaça 3D visto num CodePen público
 * (partículas billboard com textura suave, subindo e desaparecendo), mas
 * reimplementada do zero: a textura de partícula é gerada em canvas aqui
 * mesmo (gradiente radial), não baixada do asset de terceiros do CodePen.
 * Sem GSAP — a animação por partícula é um tween manual simples.
 */
export function LoginSmoke() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 1, 5000);
    camera.position.z = 900;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const light = new THREE.DirectionalLight(0xffffff, 0.6);
    light.position.set(-1, 0.5, 1);
    scene.add(light);

    // Textura de partícula: mancha suave em gradiente radial, gerada aqui —
    // não é a imagem de fumaça hospedada pelo CodePen original.
    const puffCanvas = document.createElement('canvas');
    puffCanvas.width = puffCanvas.height = 256;
    const ctx = puffCanvas.getContext('2d')!;
    const gradient = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    gradient.addColorStop(0, 'rgba(255,255,255,0.9)');
    gradient.addColorStop(0.4, 'rgba(255,255,255,0.35)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 256, 256);
    const puffTexture = new THREE.CanvasTexture(puffCanvas);

    const geometry = new THREE.PlaneGeometry(420, 420);
    const accentCyan = new THREE.Color(0x00f3ff);
    const accentBlue = new THREE.Color(0x48aeed);

    type Particle = {
      mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshLambertMaterial>;
      startTime: number;
      duration: number;
      startX: number;
      driftX: number;
    };
    const particles: Particle[] = [];
    const spawnX = () => Math.random() * width * 1.3 - width * 0.15;

    function makeParticle(initial: boolean): Particle {
      const material = new THREE.MeshLambertMaterial({
        map: puffTexture,
        color: Math.random() > 0.5 ? accentCyan : accentBlue,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const mesh = new THREE.Mesh(geometry, material);
      const x = spawnX();
      mesh.position.set(x, initial ? Math.random() * height - height * 0.3 : height * 0.7, Math.random() * 400 - 200);
      mesh.rotation.z = Math.random() * Math.PI * 2;
      mesh.scale.setScalar(0.5);
      scene.add(mesh);
      return {
        mesh,
        startTime: performance.now() - (initial ? Math.random() * 20000 : 0),
        duration: 16000 + Math.random() * 10000,
        startX: x,
        driftX: Math.random() * 160 - 80,
      };
    }

    for (let i = 0; i < 26; i++) particles.push(makeParticle(true));

    function resize() {
      if (!container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    }
    window.addEventListener('resize', resize);

    let raf = 0;
    function animate() {
      raf = requestAnimationFrame(animate);
      const now = performance.now();
      for (const p of particles) {
        let t = (now - p.startTime) / p.duration;
        if (t >= 1) {
          p.startTime = now;
          p.duration = 16000 + Math.random() * 10000;
          p.startX = spawnX();
          p.driftX = Math.random() * 160 - 80;
          t = 0;
        }
        const envelope = t < 0.35 ? t / 0.35 : (1 - t) / 0.65;
        p.mesh.material.opacity = Math.max(0, envelope) * 0.22;
        p.mesh.position.y = height * 0.7 - t * height * 1.4;
        p.mesh.position.x = p.startX + p.driftX * t;
        p.mesh.rotation.z += 0.0008;
        p.mesh.scale.setScalar(0.5 + t * 2.4);
      }
      renderer.render(scene, camera);
    }
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      for (const p of particles) {
        p.mesh.material.dispose();
      }
      geometry.dispose();
      puffTexture.dispose();
      renderer.dispose();
      container.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={containerRef} className="login__smoke" aria-hidden="true" />;
}
